import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../layout/header";
import {
  createHardwareTechnicianURL,
  createHardwareVendorURL,
  deleteHardwareTechnicianURL,
  deleteHardwareVendorURL,
  getHardwareTechniciansURL,
  getHardwareVendorsURL,
  updateHardwareTechnicianURL,
  updateHardwareVendorURL
} from "../libraries/apiUrl";
import { showToast } from "../libraries/clipboardUtils";
import { getActivityByName } from "../libraries/activityLogger";
import { getLoggedInAdminLoginId } from "../libraries/auth";
import { _delete, _get, _post, _put } from "../libraries/fetch";
import { canViewDatabaseSheetsTab } from "../libraries/privileges";
import { ContactUsSheet } from "./ContactUsPage";
import { LoginLogsSheet } from "./LoginLogsPage";

const TABS = [
  { id: "vendor", label: "Vendor" },
  { id: "technician", label: "Technician" },
  { id: "contact-us", label: "Contact Us" },
  { id: "login-logs", label: "Login Logs" }
];

const VENDOR_TABLE_HEADINGS = [
  "Sr No.",
  "Business Name",
  "Contact Person",
  "Phone",
  "Email",
  "City",
  "Category",
  "Performance"
];

const VENDOR_CATEGORY_OPTIONS = ["Devices", "Hardware", "Barriers"];

const VENDOR_PROVINCES = [
  {
    name: "Punjab",
    cities: [
      "Bahawalnagar",
      "Bahawalpur",
      "Dera Ghazi Khan",
      "Faisalabad",
      "Gujranwala",
      "Gujrat",
      "Jhang",
      "Kasur",
      "Lahore",
      "Multan",
      "Okara",
      "Rahim Yar Khan",
      "Rawalpindi",
      "Sahiwal",
      "Sargodha",
      "Sheikhupura",
      "Sialkot"
    ]
  },
  {
    name: "Sindh",
    cities: ["Hyderabad", "Karachi", "Larkana", "Sukkur"]
  },
  {
    name: "Khyber Pakhtunkhwa",
    cities: ["Abbottabad", "Mardan", "Peshawar"]
  },
  {
    name: "Balochistan",
    cities: ["Quetta"]
  },
  {
    name: "Islamabad",
    cities: ["Islamabad"]
  },
  {
    name: "Azad Kashmir",
    cities: ["Mirpur", "Muzaffarabad"]
  }
];

const vendorFieldType = (heading) => {
  if (heading === "Email") return "email";
  if (heading === "Phone") return "tel";
  return "text";
};

const TEXT_ONLY_PATTERN = /^[A-Za-z]+(?:[ ' -]+[A-Za-z]+)*$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeChoiceValues = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  const text = String(value || "").trim();
  if (!text) {
    return [];
  }

  return text.split(",").map((item) => item.trim()).filter(Boolean);
};

const formatChoiceValues = (value) => normalizeChoiceValues(value).join(", ");

const sanitizeRecordField = (heading, value, textFields) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (heading === "Phone") {
    return value.replace(/\D/g, "");
  }

  if (textFields.includes(heading)) {
    return value.replace(/[^A-Za-z ' -]/g, "");
  }

  return value;
};

const getRecordFormErrors = (form, sheet) => {
  const errors = {};
  const textFields = sheet?.textFields || [];

  textFields.forEach((heading) => {
    if (!TEXT_ONLY_PATTERN.test(String(form[heading] || "").trim())) {
      errors[heading] = "Enter text only.";
    }
  });

  if (!/^\d+$/.test(String(form.Phone || "").trim())) {
    errors.Phone = "Enter numbers only.";
  }

  if (!EMAIL_PATTERN.test(String(form.Email || "").trim())) {
    errors.Email = "Enter a valid email.";
  }

  if (sheet?.choiceMultiple) {
    const selected = normalizeChoiceValues(form[sheet.choiceHeading]);
    const allowed = sheet.choiceOptions || [];
    if (!selected.length || selected.some((item) => !allowed.includes(item))) {
      errors[sheet.choiceHeading] = "Select at least one category.";
    }
  } else if (sheet?.choiceHeading && !sheet.choiceOptions.includes(String(form[sheet.choiceHeading] || "").trim())) {
    errors[sheet.choiceHeading] = sheet.choiceFormPlaceholder || "Select an option.";
  }

  if (!String(form.City || "").trim()) {
    errors.City = "Select a city.";
  }

  return errors;
};

const SUCCESS_MESSAGES = {
  vendor: {
    load: "Vendors loaded successfully.",
    add: "Vendor added successfully.",
    update: "Vendor updated successfully.",
    delete: "Vendor deleted successfully."
  },
  technician: {
    load: "Technicians loaded successfully.",
    add: "Technician added successfully.",
    update: "Technician updated successfully.",
    delete: "Technician deleted successfully."
  }
};

const SHEET_ENDPOINTS = {
  vendor: {
    list: getHardwareVendorsURL,
    create: createHardwareVendorURL,
    update: updateHardwareVendorURL,
    remove: deleteHardwareVendorURL
  },
  technician: {
    list: getHardwareTechniciansURL,
    create: createHardwareTechnicianURL,
    update: updateHardwareTechnicianURL,
    remove: deleteHardwareTechnicianURL
  }
};

const apiErrorMessage = (response, fallback) => {
  const raw = response?.error || response?.message;
  if (Array.isArray(raw)) {
    return raw.join(" ");
  }
  if (raw && typeof raw === "object") {
    return raw.message || fallback;
  }
  return raw || fallback;
};

const isApiSuccess = (response) => {
  if (!response || response.success === false || response.status === false) {
    return false;
  }

  return response.status === true || response.success === true;
};

const extractRecords = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const recordIdFromItem = (item) => String(item?._id || item?.id || "").trim();

const mapDirectoryRecord = (sheetId, item, index) => {
  const shared = {
    id: recordIdFromItem(item),
    "Sr No.": String(index + 1),
    Phone: item?.phone || "",
    Email: item?.email || "",
    City: item?.city || "",
    performance: normalizePerformance(item?.performance)
  };

  if (sheetId === "technician") {
    return {
      ...shared,
      "Technician Name": item?.technicianName || "",
      Specialization: item?.specialization || ""
    };
  }

  return {
    ...shared,
    "Business Name": item?.businessName || item?.vendorName || "",
    "Contact Person": item?.contactPerson || "",
    Category: normalizeChoiceValues(item?.category)
  };
};

const PERFORMANCE_RATING_OPTIONS = [
  "Excellent",
  "Satisfactory",
  "Unsatisfactory"
];

const todayDateValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const normalizePerformance = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }

      const comment = String(entry.comment || "").trim();
      const date = String(entry.date || "").trim();
      if (!comment || !date) {
        return null;
      }

      const enteredBy = String(entry.enteredBy || "").trim();
      const performanceRating = PERFORMANCE_RATING_OPTIONS.includes(entry.performanceRating)
        ? entry.performanceRating
        : "";

      return {
        comment,
        date,
        ...(enteredBy ? { enteredBy } : {}),
        ...(performanceRating ? { performanceRating } : {})
      };
    })
    .filter(Boolean);
};

const directoryPayload = (sheetId, form) => {
  const shared = {
    phone: String(form.Phone || "").trim(),
    email: String(form.Email || "").trim(),
    city: String(form.City || "").trim()
  };

  if (sheetId === "technician") {
    return {
      ...shared,
      technicianName: String(form["Technician Name"] || "").trim(),
      specialization: String(form.Specialization || "").trim()
    };
  }

  return {
    ...shared,
    vendorName: String(form["Business Name"] || "").trim(),
    contactPerson: String(form["Contact Person"] || "").trim(),
    category: normalizeChoiceValues(form.Category)
  };
};

const TECHNICIAN_TABLE_HEADINGS = [
  "Sr No.",
  "Technician Name",
  "Phone",
  "Email",
  "City",
  "Specialization",
  "Performance"
];

const TECHNICIAN_SPECIALIZATION_OPTIONS = ["Devices", "Hardware", "Barriers"];

const RECORD_SHEETS = {
  vendor: {
    title: "Vendor",
    subheading: "Vendor records in DataBase Sheets.",
    headings: VENDOR_TABLE_HEADINGS,
    emptyMessage: "No vendor records found.",
    filterEmptyMessage: "No vendors match the selected filters.",
    singular: "vendor",
    singularTitle: "Vendor",
    textFields: ["Business Name", "Contact Person"],
    choiceHeading: "Category",
    choiceOptions: VENDOR_CATEGORY_OPTIONS,
    choiceMultiple: true,
    choiceFilterPlaceholder: "All categories",
    choiceFormPlaceholder: "Select categories",
    deleteDetails: ["Business Name", "Contact Person"],
    nameHeading: "Business Name"
  },
  technician: {
    title: "Technician",
    subheading: "Technician records in DataBase Sheets.",
    headings: TECHNICIAN_TABLE_HEADINGS,
    emptyMessage: "No technician records found.",
    filterEmptyMessage: "No technicians match the selected filters.",
    singular: "technician",
    singularTitle: "Technician",
    textFields: ["Technician Name"],
    choiceHeading: "Specialization",
    choiceOptions: TECHNICIAN_SPECIALIZATION_OPTIONS,
    choiceFilterPlaceholder: "All specializations",
    choiceFormPlaceholder: "Select specialization",
    deleteDetails: ["Technician Name", "Phone"],
    nameHeading: "Technician Name"
  }
};

const emptyRecordForm = (headings) =>
  Object.fromEntries(headings.map((heading) => [heading, ""]));

const recordFormFields = (headings) =>
  headings.filter((heading) => heading !== "Sr No." && heading !== "Performance");

function VendorChoiceMenu({
  label,
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onSelect,
  fieldStyle,
  error,
  multiple = false
}) {
  const selectedValues = multiple ? normalizeChoiceValues(value) : [];
  const selectedLabel = multiple
    ? selectedValues
      .map((item) => options.find((option) => option.value === item)?.label || item)
      .join(", ")
    : options.find((option) => option.value === value)?.label || value;
  const hasSelection = multiple ? selectedValues.length > 0 : Boolean(value);

  return (
    <div style={fieldStyle} data-vendor-menu="">
      {label}
      <div className="vendor-category">
        <button
          type="button"
          className="vendor-category-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span className={hasSelection ? "vendor-category-value" : "vendor-category-placeholder"}>
            {selectedLabel || placeholder}
          </span>
          <span className={`vendor-category-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true" />
        </button>
        {isOpen && (
          <ul
            className="vendor-category-menu"
            role="listbox"
            aria-label={label}
            aria-multiselectable={multiple || undefined}
          >
            {options.map((option) => {
              const isSelected = multiple
                ? selectedValues.includes(option.value)
                : value === option.value;

              return (
                <li key={option.value || option.label} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`vendor-category-option${isSelected ? " is-selected" : ""}`}
                    onClick={() => onSelect(option.value)}
                  >
                    {multiple && (
                      <span
                        className={`vendor-category-check${isSelected ? " is-checked" : ""}`}
                        aria-hidden="true"
                      />
                    )}
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {error && <span style={fieldErrorStyle}>{error}</span>}
    </div>
  );
}

function VendorCityFilter({
  label = "City",
  placeholder = "All cities",
  includeAllOption = true,
  fieldStyle = filterFieldStyle,
  value,
  isOpen,
  provinceName,
  onToggle,
  onOpenProvince,
  onSelect,
  error
}) {
  const province = VENDOR_PROVINCES.find((item) => item.name === provinceName);

  return (
    <div style={fieldStyle} data-vendor-menu="">
      {label}
      <div className="vendor-category">
        <button
          type="button"
          className="vendor-category-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span className={value ? "vendor-category-value" : "vendor-category-placeholder"}>
            {value || placeholder}
          </span>
          <span className={`vendor-category-chevron${isOpen ? " is-open" : ""}`} aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="vendor-city-menus">
            <ul className="vendor-category-menu" role="listbox" aria-label="Provinces">
              {includeAllOption && (
                <li role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={!value}
                    className={`vendor-category-option${!value ? " is-selected" : ""}`}
                    onClick={() => onSelect("")}
                  >
                    All cities
                  </button>
                </li>
              )}
              {VENDOR_PROVINCES.map((item) => (
                <li key={item.name} role="presentation">
                  <button
                    type="button"
                    className={`vendor-category-option vendor-province-option${provinceName === item.name ? " is-selected" : ""}`}
                    aria-expanded={provinceName === item.name}
                    onClick={() => onOpenProvince(item.name)}
                  >
                    <span>{item.name}</span>
                    <span className="vendor-province-arrow" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            {province && (
              <ul className="vendor-category-menu" role="listbox" aria-label={`${province.name} cities`}>
                {province.cities.map((city) => (
                  <li key={city} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={value === city}
                      className={`vendor-category-option${value === city ? " is-selected" : ""}`}
                      onClick={() => onSelect(city)}
                    >
                      {city}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {error && <span style={fieldErrorStyle}>{error}</span>}
    </div>
  );
}

function DatabaseSheetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => canViewDatabaseSheetsTab(tab.id)),
    []
  );
  const requestedTab = searchParams.get("tab");
  const defaultTab = visibleTabs[0]?.id || "vendor";
  const activeTab = visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : defaultTab;
  const sheet = RECORD_SHEETS[activeTab];
  const [recordsBySheet, setRecordsBySheet] = useState({ vendor: [], technician: [] });
  const [loadingSheet, setLoadingSheet] = useState("");
  const [errorsBySheet, setErrorsBySheet] = useState({ vendor: "", technician: "" });
  const [actionError, setActionError] = useState("");
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [commentsSheetId, setCommentsSheetId] = useState("");
  const [commentsTargetId, setCommentsTargetId] = useState("");
  const [commentsText, setCommentsText] = useState("");
  const [commentsDate, setCommentsDate] = useState(todayDateValue);
  const [commentsRating, setCommentsRating] = useState("");
  const [commentsErrors, setCommentsErrors] = useState({});
  const [isSavingComments, setIsSavingComments] = useState(false);
  const [performanceRecord, setPerformanceRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editorMode, setEditorMode] = useState("");
  const [editorSheetId, setEditorSheetId] = useState("");
  const [editingRecordId, setEditingRecordId] = useState("");
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteSheetId, setDeleteSheetId] = useState("");
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [isConfirmUpdateOpen, setIsConfirmUpdateOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({});
  const [recordFormErrors, setRecordFormErrors] = useState({});
  const [openMenu, setOpenMenu] = useState("");
  const [choiceFilter, setChoiceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [cityMenuProvince, setCityMenuProvince] = useState("");
  const [formCityProvince, setFormCityProvince] = useState("");
  const editorSheet = RECORD_SHEETS[editorSheetId];
  const deleteSheet = RECORD_SHEETS[deleteSheetId];
  const visibleRecords = useMemo(() => {
    if (!sheet) {
      return [];
    }

    const selectedCity = cityFilter.trim().toLowerCase();
    const records = recordsBySheet[activeTab] || [];

    return records.filter((record) => {
      const choiceMatches = !choiceFilter
        || normalizeChoiceValues(record[sheet.choiceHeading]).includes(choiceFilter);
      const cityMatches = !selectedCity
        || String(record.City || "").trim().toLowerCase() === selectedCity;
      return choiceMatches && cityMatches;
    });
  }, [activeTab, choiceFilter, cityFilter, recordsBySheet, sheet]);
  const loadTokenRef = useRef({ vendor: 0, technician: 0 });
  const isSheetLoading = Boolean(sheet) && loadingSheet === activeTab;
  const sheetError = errorsBySheet[activeTab] || "";
  const tableEmptyMessage = isSheetLoading
    ? `Loading ${sheet.singular} records...`
    : sheetError
      ? sheetError
      : sheet && (recordsBySheet[activeTab] || []).length > 0 && visibleRecords.length === 0
        ? sheet.filterEmptyMessage
        : sheet?.emptyMessage;

  const loadRecords = useCallback(async (sheetId, { notify = false } = {}) => {
    const endpoints = SHEET_ENDPOINTS[sheetId];
    if (!endpoints) {
      return;
    }

    const requestToken = (loadTokenRef.current[sheetId] || 0) + 1;
    loadTokenRef.current[sheetId] = requestToken;
    setLoadingSheet(sheetId);
    setErrorsBySheet((current) => ({ ...current, [sheetId]: "" }));

    try {
      const response = await _get(endpoints.list(), { useCache: false });
      if (loadTokenRef.current[sheetId] !== requestToken) {
        return;
      }
      if (!isApiSuccess(response)) {
        throw new Error(apiErrorMessage(response, `Failed to load ${sheetId} records.`));
      }

      const records = extractRecords(response).map((item, index) =>
        mapDirectoryRecord(sheetId, item, index)
      );
      setRecordsBySheet((current) => ({ ...current, [sheetId]: records }));
      if (notify) {
        showToast(SUCCESS_MESSAGES[sheetId]?.load || "Records loaded successfully.", "success");
      }
    } catch (error) {
      if (loadTokenRef.current[sheetId] !== requestToken) {
        return;
      }
      setErrorsBySheet((current) => ({
        ...current,
        [sheetId]: error?.message || `Failed to load ${sheetId} records.`
      }));
    } finally {
      if (loadTokenRef.current[sheetId] === requestToken) {
        setLoadingSheet((current) => (current === sheetId ? "" : current));
      }
    }
  }, []);

  useEffect(() => {
    if (visibleTabs.length && requestedTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, requestedTab, setSearchParams, visibleTabs.length]);

  useEffect(() => {
    if (activeTab === "vendor" || activeTab === "technician") {
      loadRecords(activeTab, { notify: true });
    }
  }, [activeTab, loadRecords]);

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!event.target.closest("[data-vendor-menu]")) {
        setOpenMenu("");
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenMenu("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const closeRecordEditor = () => {
    setEditorMode("");
    setEditorSheetId("");
    setEditingRecordId("");
    setIsConfirmSaveOpen(false);
    setIsConfirmUpdateOpen(false);
    setOpenMenu("");
    setFormCityProvince("");
    setRecordFormErrors({});
    setActionError("");
  };

  const setActiveTab = (tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
    closeRecordEditor();
    setRecordToDelete(null);
    setDeleteSheetId("");
    setActionError("");
    setIsCommentsOpen(false);
    setCommentsErrors({});
    setPerformanceRecord(null);
    setChoiceFilter("");
    setCityFilter("");
    setCityMenuProvince("");
    setOpenMenu("");
  };

  const openAddRecord = () => {
    if (!sheet) {
      return;
    }

    setRecordForm(emptyRecordForm(sheet.headings));
    setEditorMode("add");
    setEditorSheetId(activeTab);
    setEditingRecordId("");
    setRecordToDelete(null);
    setDeleteSheetId("");
    setIsConfirmSaveOpen(false);
    setIsConfirmUpdateOpen(false);
    setOpenMenu("");
    setFormCityProvince("");
    setRecordFormErrors({});
    setActionError("");
  };

  const openUpdateRecord = (record) => {
    if (!sheet) {
      return;
    }

    setRecordForm({
      ...emptyRecordForm(sheet.headings),
      ...record
    });
    setEditorMode("update");
    setEditorSheetId(activeTab);
    setEditingRecordId(record.id);
    setRecordToDelete(null);
    setDeleteSheetId("");
    setIsConfirmSaveOpen(false);
    setIsConfirmUpdateOpen(false);
    setOpenMenu("");
    setFormCityProvince("");
    setRecordFormErrors({});
    setActionError("");
  };

  const openDeleteRecord = (record) => {
    setRecordToDelete(record);
    setDeleteSheetId(activeTab);
    setIsConfirmSaveOpen(false);
    setIsConfirmUpdateOpen(false);
    setOpenMenu("");
    setActionError("");
  };

  const toggleMenu = (menuId) => {
    setOpenMenu((current) => (current === menuId ? "" : menuId));
    if (menuId === "filter-city") {
      setCityMenuProvince("");
    }
    if (menuId === "form-city") {
      setFormCityProvince("");
    }
  };

  const handleRecordFieldChange = (heading, value) => {
    const nextValue = sanitizeRecordField(heading, value, editorSheet?.textFields || []);
    setRecordForm((current) => ({ ...current, [heading]: nextValue }));
    setRecordFormErrors((current) => {
      if (!current[heading]) {
        return current;
      }
      const nextErrors = { ...current };
      delete nextErrors[heading];
      return nextErrors;
    });
  };

  const handleSaveRecordClick = (event) => {
    event.preventDefault();
    setOpenMenu("");
    setFormCityProvince("");
    const errors = getRecordFormErrors(recordForm, editorSheet);
    setRecordFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    setActionError("");
    if (editorMode === "update") {
      setIsConfirmUpdateOpen(true);
      return;
    }
    setIsConfirmSaveOpen(true);
  };

  const persistRecord = async (sheetId, request) => {
    const endpoints = SHEET_ENDPOINTS[sheetId];
    if (!endpoints) {
      throw new Error("This sheet is not available.");
    }

    const response = await request(endpoints);
    if (!isApiSuccess(response)) {
      throw new Error(apiErrorMessage(response, "Request failed. Please try again."));
    }

    await loadRecords(sheetId);
  };

  const handleConfirmSaveRecord = async () => {
    if (isSaving) {
      return;
    }

    const sheetId = editorSheetId;
    setIsSaving(true);
    setActionError("");

    try {
      await persistRecord(sheetId, (endpoints) =>
        _post(endpoints.create(), directoryPayload(sheetId, recordForm))
      );
      closeRecordEditor();
      showToast(SUCCESS_MESSAGES[sheetId]?.add || "Record added successfully.", "success");
    } catch (error) {
      setActionError(error?.message || "Failed to save record.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmUpdateRecord = async () => {
    if (isSaving || !editingRecordId) {
      return;
    }

    const sheetId = editorSheetId;
    setIsSaving(true);
    setActionError("");

    try {
      await persistRecord(sheetId, (endpoints) =>
        _put(endpoints.update(editingRecordId), directoryPayload(sheetId, recordForm))
      );
      closeRecordEditor();
      showToast(SUCCESS_MESSAGES[sheetId]?.update || "Record updated successfully.", "success");
    } catch (error) {
      setActionError(error?.message || "Failed to update record.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDeleteRecord = async () => {
    if (isDeleting || !recordToDelete?.id) {
      return;
    }

    const sheetId = deleteSheetId;
    setIsDeleting(true);
    setActionError("");

    try {
      await persistRecord(sheetId, (endpoints) =>
        _delete(endpoints.remove(recordToDelete.id))
      );
      setRecordToDelete(null);
      setDeleteSheetId("");
      showToast(SUCCESS_MESSAGES[sheetId]?.delete || "Record deleted successfully.", "success");
    } catch (error) {
      setActionError(error?.message || "Failed to delete record.");
    } finally {
      setIsDeleting(false);
    }
  };

  const openComments = () => {
    if (!sheet) {
      return;
    }

    setCommentsSheetId(activeTab);
    setCommentsTargetId("");
    setCommentsText("");
    setCommentsDate(todayDateValue());
    setCommentsRating("");
    setCommentsErrors({});
    setIsCommentsOpen(true);
    setOpenMenu("");
  };

  const closeComments = () => {
    setIsCommentsOpen(false);
    setCommentsErrors({});
    setOpenMenu("");
  };

  const handleSaveComments = async () => {
    if (isSavingComments) {
      return;
    }

    const sheetId = commentsSheetId;
    const commentsSheetConfig = RECORD_SHEETS[sheetId];
    const record = (recordsBySheet[sheetId] || []).find((item) => item.id === commentsTargetId);
    const nextErrors = {};

    if (!record) {
      nextErrors.person = `Select a ${commentsSheetConfig?.singular || "record"}.`;
    }

    if (!PERFORMANCE_RATING_OPTIONS.includes(commentsRating)) {
      nextErrors.rating = "Select a performance rating.";
    }

    if (!String(commentsText || "").trim()) {
      nextErrors.comment = "Enter a comment.";
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(commentsDate || ""))) {
      nextErrors.date = "Select a date.";
    }

    setCommentsErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSavingComments(true);

    try {
      const performance = [
        ...normalizePerformance(record.performance),
        {
          comment: commentsText.trim(),
          date: commentsDate,
          performanceRating: commentsRating,
          enteredBy: getActivityByName() || getLoggedInAdminLoginId() || "Unknown"
        }
      ];

      await persistRecord(sheetId, (endpoints) =>
        _put(endpoints.update(record.id), {
          ...directoryPayload(sheetId, record),
          performance
        })
      );
      closeComments();
      showToast(
        `${commentsSheetConfig?.singularTitle || "Record"} performance updated successfully.`,
        "success"
      );
    } catch (error) {
      setCommentsErrors({
        form: error?.message || "Failed to update performance."
      });
    } finally {
      setIsSavingComments(false);
    }
  };

  const tableHeadings = sheet ? [...sheet.headings, "Action"] : [];
  const isRecordEditorOpen = Boolean(editorMode && editorSheet);

  return (
    <div className="main">
      <Header />
      <main style={pageStyle}>
        <div className="branchMenu" aria-label="DataBase Sheets tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "active" : ""}
              style={activeTab === tab.id ? selectedTabStyle : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="contentArea"
          style={tabContentStyle}
        >
          {sheet && (
            <>
              <div style={headerStyle}>
                <div style={headerRowStyle}>
                  <div>
                    <h1 style={headingStyle}>{sheet.title}</h1>
                    <p style={subheadingStyle}>{sheet.subheading}</p>
                  </div>
                  <div style={headerActionsStyle}>
                    <button
                      type="button"
                      style={commentsButtonStyle}
                      onClick={openComments}
                      disabled={isSheetLoading || isSaving || isDeleting || isSavingComments}
                    >
                      Performance
                    </button>
                    <button
                      type="button"
                      style={isSheetLoading || isSaving ? { ...addVendorButtonStyle, opacity: 0.7, cursor: "wait" } : addVendorButtonStyle}
                      onClick={openAddRecord}
                      disabled={isSheetLoading || isSaving || isDeleting}
                    >
                      Add {sheet.singularTitle}
                    </button>
                  </div>
                </div>
              </div>

              {sheet && (
                <div style={filterBarStyle}>
                  <VendorChoiceMenu
                    label={sheet.choiceHeading}
                    value={choiceFilter}
                    placeholder={sheet.choiceFilterPlaceholder}
                    options={[
                      { value: "", label: sheet.choiceFilterPlaceholder },
                      ...sheet.choiceOptions.map((option) => ({ value: option, label: option }))
                    ]}
                    isOpen={openMenu === "filter-choice"}
                    onToggle={() => toggleMenu("filter-choice")}
                    onSelect={(value) => {
                      setChoiceFilter(value);
                      setOpenMenu("");
                    }}
                    fieldStyle={filterFieldStyle}
                  />
                  <VendorCityFilter
                    value={cityFilter}
                    isOpen={openMenu === "filter-city"}
                    provinceName={cityMenuProvince}
                    onToggle={() => toggleMenu("filter-city")}
                    onOpenProvince={setCityMenuProvince}
                    onSelect={(value) => {
                      setCityFilter(value);
                      setCityMenuProvince("");
                      setOpenMenu("");
                    }}
                  />
                </div>
              )}

              {sheetError && visibleRecords.length > 0 && (
                <p style={sheetBannerStyle} role="alert">{sheetError}</p>
              )}

              <div style={tableShellStyle}>
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={tableHeadRowStyle}>
                        {tableHeadings.map((heading) => (
                          <th key={heading} style={tableHeaderStyle}>
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRecords.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tableHeadings.length}
                            style={sheetError && !isSheetLoading ? { ...emptyStyle, color: "#fca5a5" } : emptyStyle}
                          >
                            {tableEmptyMessage}
                          </td>
                        </tr>
                      ) : (
                        visibleRecords.map((record) => (
                          <tr key={record.id} style={tableBodyRowStyle}>
                            {sheet.headings.map((heading) => (
                              <td key={heading} style={tableCellStyle}>
                                {heading === "Performance" ? (
                                  <button
                                    type="button"
                                    style={chatIconButtonStyle}
                                    onClick={() => setPerformanceRecord(record)}
                                    title="View comments"
                                    aria-label={`View ${record[sheet.nameHeading] || sheet.singular} comments`}
                                  >
                                    <svg viewBox="0 0 24 24" aria-hidden="true" style={chatIconSvgStyle}>
                                      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
                                      <line x1="8" y1="9" x2="16" y2="9"></line>
                                      <line x1="8" y1="13" x2="14" y2="13"></line>
                                    </svg>
                                  </button>
                                ) : (
                                  (Array.isArray(record[heading])
                                    ? formatChoiceValues(record[heading])
                                    : record[heading]) || "—"
                                )}
                              </td>
                            ))}
                            <td style={tableCellStyle}>
                              <div style={rowActionStyle}>
                                <button
                                  type="button"
                                  style={updateRowButtonStyle}
                                  onClick={() => openUpdateRecord(record)}
                                  disabled={isSaving || isDeleting || isSheetLoading}
                                >
                                  Update
                                </button>
                                <button
                                  type="button"
                                  style={deleteRowButtonStyle}
                                  onClick={() => openDeleteRecord(record)}
                                  disabled={isSaving || isDeleting || isSheetLoading}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === "contact-us" && <ContactUsSheet />}
          {activeTab === "login-logs" && <LoginLogsSheet />}
        </div>
      </main>

      {isRecordEditorOpen && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={editorMode === "update" ? `Update ${editorSheet.singular}` : `Add ${editorSheet.singular}`}
        >
          <div style={{
            ...modalCardStyle,
            overflow: openMenu === "form-choice" || openMenu === "form-city" ? "visible" : "auto"
          }}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  {editorMode === "update" ? `Update ${editorSheet.singularTitle}` : `Add ${editorSheet.singularTitle}`}
                </h2>
                <p style={modalSubtitleStyle}>
                  {editorMode === "update"
                    ? `Change the ${editorSheet.singular} details below.`
                    : `Enter the ${editorSheet.singular} details below.`}
                </p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeRecordEditor} aria-label="Close record form">
                X
              </button>
            </div>

            <form onSubmit={handleSaveRecordClick} noValidate>
              {recordFormFields(editorSheet.headings).map((heading) => (
                heading === editorSheet.choiceHeading ? (
                  <VendorChoiceMenu
                    key={heading}
                    label={heading}
                    value={recordForm[heading]}
                    placeholder={editorSheet.choiceFormPlaceholder}
                    options={editorSheet.choiceOptions.map((option) => ({ value: option, label: option }))}
                    multiple={Boolean(editorSheet.choiceMultiple)}
                    isOpen={openMenu === "form-choice"}
                    onToggle={() => toggleMenu("form-choice")}
                    onSelect={(value) => {
                      if (editorSheet.choiceMultiple) {
                        const current = normalizeChoiceValues(recordForm[heading]);
                        const next = current.includes(value)
                          ? current.filter((item) => item !== value)
                          : [...current, value];
                        handleRecordFieldChange(heading, next);
                        return;
                      }

                      handleRecordFieldChange(heading, value);
                      setOpenMenu("");
                    }}
                    fieldStyle={fieldLabelStyle}
                    error={recordFormErrors[heading]}
                  />
                ) : heading === "City" ? (
                  <VendorCityFilter
                    key={heading}
                    label={heading}
                    placeholder="Select city"
                    includeAllOption={false}
                    fieldStyle={fieldLabelStyle}
                    value={recordForm.City}
                    isOpen={openMenu === "form-city"}
                    provinceName={formCityProvince}
                    onToggle={() => toggleMenu("form-city")}
                    onOpenProvince={setFormCityProvince}
                    onSelect={(value) => {
                      handleRecordFieldChange("City", value);
                      setFormCityProvince("");
                      setOpenMenu("");
                    }}
                    error={recordFormErrors.City}
                  />
                ) : (
                  <label key={heading} style={fieldLabelStyle}>
                    {heading}
                    <input
                      type={vendorFieldType(heading)}
                      value={recordForm[heading] || ""}
                      onChange={(event) => handleRecordFieldChange(heading, event.target.value)}
                      placeholder={heading === "Email" ? "name@email.com" : `Enter ${heading.toLowerCase()}`}
                      inputMode={heading === "Phone" ? "numeric" : undefined}
                      style={formInputStyle}
                      aria-invalid={Boolean(recordFormErrors[heading])}
                    />
                    {recordFormErrors[heading] && (
                      <span style={fieldErrorStyle}>{recordFormErrors[heading]}</span>
                    )}
                  </label>
                )
              ))}

              <div style={modalActionsStyle}>
                <button type="button" style={secondaryButtonStyle} onClick={closeRecordEditor}>
                  Cancel
                </button>
                <button type="submit" style={primaryButtonStyle}>
                  {editorMode === "update" ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isConfirmSaveOpen && editorSheet && (
        <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label={`Confirm save ${editorSheet.singular}`}>
          <div style={confirmCardStyle}>
            <h3 style={confirmTitleStyle}>Save {editorSheet.singularTitle}?</h3>
            <p style={confirmTextStyle}>Are you sure you want to save this {editorSheet.singular}?</p>
            {actionError && <p style={actionErrorStyle} role="alert">{actionError}</p>}
            <div style={confirmActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmSaveOpen(false)} disabled={isSaving}>
                No
              </button>
              <button type="button" style={primaryButtonStyle} onClick={handleConfirmSaveRecord} disabled={isSaving}>
                {isSaving ? "Saving..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isConfirmUpdateOpen && editorSheet && (
        <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label={`Confirm update ${editorSheet.singular}`}>
          <div style={confirmCardStyle}>
            <h3 style={confirmTitleStyle}>Update {editorSheet.singularTitle}?</h3>
            <p style={confirmTextStyle}>Are you sure you want to update this {editorSheet.singular}?</p>
            {actionError && <p style={actionErrorStyle} role="alert">{actionError}</p>}
            <div style={confirmActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setIsConfirmUpdateOpen(false)} disabled={isSaving}>
                No
              </button>
              <button type="button" style={primaryButtonStyle} onClick={handleConfirmUpdateRecord} disabled={isSaving}>
                {isSaving ? "Updating..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {recordToDelete && deleteSheet && (
        <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label={`Confirm delete ${deleteSheet.singular}`}>
          <div style={deleteConfirmCardStyle}>
            <h3 style={confirmTitleStyle}>Delete {deleteSheet.singularTitle}?</h3>
            <div style={vendorDetailsStyle}>
              {deleteSheet.deleteDetails.map((heading) => (
                <div key={heading} style={vendorDetailRowStyle}>
                  <span style={vendorDetailLabelStyle}>{heading}</span>
                  <span style={vendorDetailValueStyle}>{recordToDelete[heading] || "—"}</span>
                </div>
              ))}
            </div>
            <p style={confirmTextStyle}>Are you sure you want to delete this {deleteSheet.singular}?</p>
            {actionError && <p style={actionErrorStyle} role="alert">{actionError}</p>}
            <div style={confirmActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setRecordToDelete(null)} disabled={isDeleting}>
                No
              </button>
              <button type="button" style={deleteRowButtonStyle} onClick={handleConfirmDeleteRecord} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCommentsOpen && RECORD_SHEETS[commentsSheetId] && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={`${RECORD_SHEETS[commentsSheetId].singularTitle} comments`}
        >
          <div style={{
            ...modalCardStyle,
            overflow: openMenu === "comments-person" || openMenu === "comments-rating" ? "visible" : "auto"
          }}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>Comments</h2>
                <p style={modalSubtitleStyle}>
                  Add a performance comment for a {RECORD_SHEETS[commentsSheetId].singular}.
                </p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeComments} aria-label="Close comments">
                X
              </button>
            </div>

            <VendorChoiceMenu
              label={RECORD_SHEETS[commentsSheetId].singularTitle}
              value={commentsTargetId}
              placeholder={`Select ${RECORD_SHEETS[commentsSheetId].singular}`}
              options={(recordsBySheet[commentsSheetId] || []).map((record) => ({
                value: record.id,
                label: record[RECORD_SHEETS[commentsSheetId].nameHeading] || "Untitled"
              }))}
              isOpen={openMenu === "comments-person"}
              onToggle={() => toggleMenu("comments-person")}
              onSelect={(value) => {
                setCommentsTargetId(value);
                setOpenMenu("");
                setCommentsErrors((current) => {
                  if (!current.person) {
                    return current;
                  }
                  const nextErrors = { ...current };
                  delete nextErrors.person;
                  return nextErrors;
                });
              }}
              fieldStyle={fieldLabelStyle}
              error={commentsErrors.person}
            />

            <VendorChoiceMenu
              label="Performance Rating"
              value={commentsRating}
              placeholder="Select rating"
              options={PERFORMANCE_RATING_OPTIONS.map((option) => ({
                value: option,
                label: option
              }))}
              isOpen={openMenu === "comments-rating"}
              onToggle={() => toggleMenu("comments-rating")}
              onSelect={(value) => {
                setCommentsRating(value);
                setOpenMenu("");
                setCommentsErrors((current) => {
                  if (!current.rating) {
                    return current;
                  }
                  const nextErrors = { ...current };
                  delete nextErrors.rating;
                  return nextErrors;
                });
              }}
              fieldStyle={fieldLabelStyle}
              error={commentsErrors.rating}
            />

            <label style={fieldLabelStyle}>
              Comments
              <textarea
                value={commentsText}
                onChange={(event) => {
                  setCommentsText(event.target.value);
                  setCommentsErrors((current) => {
                    if (!current.comment) {
                      return current;
                    }
                    const nextErrors = { ...current };
                    delete nextErrors.comment;
                    return nextErrors;
                  });
                }}
                placeholder="Enter comments"
                rows={4}
                style={commentsTextareaStyle}
                aria-invalid={Boolean(commentsErrors.comment)}
              />
              {commentsErrors.comment && (
                <span style={fieldErrorStyle}>{commentsErrors.comment}</span>
              )}
            </label>

            <label style={fieldLabelStyle}>
              Date
              <input
                type="date"
                value={commentsDate}
                onChange={(event) => {
                  setCommentsDate(event.target.value);
                  setCommentsErrors((current) => {
                    if (!current.date) {
                      return current;
                    }
                    const nextErrors = { ...current };
                    delete nextErrors.date;
                    return nextErrors;
                  });
                }}
                style={formInputStyle}
                aria-invalid={Boolean(commentsErrors.date)}
              />
              {commentsErrors.date && (
                <span style={fieldErrorStyle}>{commentsErrors.date}</span>
              )}
            </label>

            {commentsErrors.form && (
              <p style={actionErrorStyle} role="alert">{commentsErrors.form}</p>
            )}

            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={closeComments} disabled={isSavingComments}>
                Cancel
              </button>
              <button type="button" style={primaryButtonStyle} onClick={handleSaveComments} disabled={isSavingComments}>
                {isSavingComments ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {performanceRecord && sheet && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={`${performanceRecord[sheet.nameHeading] || sheet.singularTitle} comments`}
        >
          <div style={{ ...modalCardStyle, width: "min(100%, 720px)" }}>
            <div style={modalHeaderStyle}>
              <h2 style={{ ...modalTitleStyle, margin: 0 }}>
                {`${performanceRecord[sheet.nameHeading] || sheet.singularTitle} - Performance`}
              </h2>
              <button
                type="button"
                style={closeButtonStyle}
                onClick={() => setPerformanceRecord(null)}
                aria-label="Close comments"
              >
                X
              </button>
            </div>
            <div style={tableShellStyle}>
              <table style={{ ...tableStyle, minWidth: "100%" }}>
                <thead>
                  <tr style={tableHeadRowStyle}>
                    {["Sr No.", "Performance Rating", "Comment", "Date", "Entered By"].map((heading) => (
                      <th key={heading} style={tableHeaderStyle}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {normalizePerformance(performanceRecord.performance).length === 0 ? (
                    <tr>
                      <td colSpan={5} style={emptyStyle}>No comments found.</td>
                    </tr>
                  ) : (
                    normalizePerformance(performanceRecord.performance).map((entry, index) => (
                      <tr key={`${entry.date}-${entry.comment}-${index}`} style={tableBodyRowStyle}>
                        <td style={tableCellStyle}>{index + 1}</td>
                        <td style={tableCellStyle}>{entry.performanceRating || "—"}</td>
                        <td style={commentTableCellStyle}>{entry.comment}</td>
                        <td style={tableCellStyle}>{entry.date}</td>
                        <td style={tableCellStyle}>{entry.enteredBy || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  padding: "clamp(16px, 3vw, 24px) clamp(12px, 3vw, 20px)",
  maxWidth: "100%",
  boxSizing: "border-box",
  background: "linear-gradient(135deg, #1e2127 0%, #2a2d35 100%)"
};

const selectedTabStyle = {
  color: "#fff"
};

const tabContentStyle = {
  padding: "clamp(14px, 3vw, 24px)",
  maxWidth: "100%",
  boxSizing: "border-box"
};

const headerStyle = {
  marginBottom: "20px"
};

const headerRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap"
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const filterBarStyle = {
  display: "flex",
  alignItems: "flex-end",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "16px",
  position: "relative",
  zIndex: 4
};

const filterFieldStyle = {
  display: "block",
  minWidth: "min(220px, 100%)",
  flex: "1 1 180px",
  maxWidth: "100%",
  boxSizing: "border-box",
  marginBottom: 0,
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 700
};

const commentsButtonStyle = {
  border: "1px solid #3a3f4b",
  background: "#1a1d23",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0
};

const addVendorButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const headingStyle = {
  margin: "0 0 8px 0",
  fontSize: "28px",
  fontWeight: 700,
  color: "var(--home-heading, #fff)"
};

const subheadingStyle = {
  margin: 0,
  fontSize: "15px",
  color: "var(--home-muted, #888)"
};

const tableShellStyle = {
  overflowX: "auto",
  border: "1px solid #3a3f4b",
  borderRadius: "10px",
  backgroundColor: "#1a1d23",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
};

const tableStyle = {
  width: "100%",
  minWidth: "1100px",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "#102235",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 12px",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textAlign: "left",
  whiteSpace: "nowrap"
};

const emptyStyle = {
  padding: "42px 20px",
  color: "#9ca3af",
  textAlign: "center"
};

const tableBodyRowStyle = {
  borderBottom: "1px solid #2f3542"
};

const tableCellStyle = {
  padding: "14px 12px",
  color: "#e5e7eb",
  fontSize: "14px",
  whiteSpace: "nowrap"
};

const commentTableCellStyle = {
  ...tableCellStyle,
  whiteSpace: "normal",
  minWidth: "240px"
};

const chatIconButtonStyle = {
  width: "30px",
  height: "30px",
  padding: 0,
  border: "1px solid rgba(74, 222, 128, 0.45)",
  borderRadius: "50%",
  backgroundColor: "rgba(24, 119, 24, 0.18)",
  color: "#4ade80",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};

const chatIconSvgStyle = {
  width: "16px",
  height: "16px",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

const commentsTextareaStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
  minHeight: "110px",
  fontFamily: "inherit"
};

const rowActionStyle = {
  display: "flex",
  gap: "8px"
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  backdropFilter: "blur(4px)"
};

const modalCardStyle = {
  width: "min(100%, 520px)",
  maxHeight: "min(90vh, 760px)",
  overflowY: "auto",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
  color: "var(--home-heading, #fff)"
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "20px"
};

const modalTitleStyle = {
  margin: "0 0 6px 0",
  fontSize: "24px",
  fontWeight: 800
};

const modalSubtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px"
};

const closeButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "10px",
  width: "36px",
  height: "36px",
  cursor: "pointer",
  fontWeight: 800
};

const fieldLabelStyle = {
  display: "block",
  marginBottom: "16px",
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 700
};

const fieldErrorStyle = {
  display: "block",
  marginTop: "6px",
  color: "#fca5a5",
  fontSize: "12px",
  fontWeight: 700
};

const formInputStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const modalActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "8px"
};

const primaryButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const secondaryButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

const updateRowButtonStyle = {
  ...primaryButtonStyle,
  padding: "8px 12px",
  fontSize: "13px"
};

const deleteRowButtonStyle = {
  border: "1px solid #dc2626",
  background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "8px 12px",
  fontSize: "13px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)"
};

const confirmOverlayStyle = {
  ...modalOverlayStyle,
  zIndex: 2100
};

const confirmCardStyle = {
  ...modalCardStyle,
  width: "min(100%, 360px)",
  maxHeight: "none",
  overflowY: "visible",
  textAlign: "center"
};

const confirmTitleStyle = {
  margin: "0 0 8px 0",
  fontSize: "22px",
  fontWeight: 800
};

const confirmTextStyle = {
  margin: "0 0 22px 0",
  color: "var(--home-muted, #888)",
  fontSize: "15px"
};

const actionErrorStyle = {
  margin: "-8px 0 18px 0",
  color: "#fca5a5",
  fontSize: "14px",
  fontWeight: 700
};

const sheetBannerStyle = {
  margin: "0 0 16px 0",
  color: "#fca5a5",
  fontSize: "14px",
  fontWeight: 700
};

const confirmActionsStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "12px"
};

const deleteConfirmCardStyle = {
  ...confirmCardStyle,
  width: "min(100%, 460px)",
  maxHeight: "min(90vh, 640px)",
  overflowY: "auto"
};

const vendorDetailsStyle = {
  margin: "0 0 18px 0",
  padding: "8px 14px",
  textAlign: "left",
  borderRadius: "12px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  border: "1px solid var(--home-border, #444)"
};

const vendorDetailRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  padding: "8px 0",
  borderBottom: "1px solid #2f3542",
  fontSize: "14px"
};

const vendorDetailLabelStyle = {
  color: "#9ca3af",
  fontWeight: 700
};

const vendorDetailValueStyle = {
  color: "#fff",
  fontWeight: 600,
  textAlign: "right"
};

export default DatabaseSheetsPage;
